import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { InvoiceVat } from './invoice-vat.js';
import { InvoiceProvider } from './invoice-provider.js';
import { InvoicingProtocol } from './invoicing-protocol.js';
import { VersionRange } from '../../core/version-range.js';

export class BillingoInvoicingProtocol extends InvoicingProtocol {
  static readonly TYPE_ID: string = 'c41400e6263c49d896a5f3077e62d2e8';
  static readonly SCHEMA_VERSION: number = 5;

  public readonly provider: InvoiceProvider = InvoiceProvider.Billingo;

  private constructor(
    public readonly accountId: string,
    public readonly apiKeySecret: string | null,
    public readonly invoiceTemplateId: string | null,
    public readonly receiptTemplateId: string | null,
    public readonly vat: InvoiceVat,
    public readonly allowLegalEntities: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return BillingoInvoicingProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(3, BillingoInvoicingProtocol.SCHEMA_VERSION);
  }

  static create(args: {
    accountId: string,
    apiKeySecret: string | null,
    invoiceTemplateId: string | null,
    receiptTemplateId: string | null,
    vat: InvoiceVat,
    allowLegalEntities: boolean,
  }): BillingoInvoicingProtocol {
    return new BillingoInvoicingProtocol(
        args.accountId,
        args.apiKeySecret,
        args.invoiceTemplateId,
        args.receiptTemplateId,
        args.vat,
        args.allowLegalEntities,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BillingoInvoicingProtocol {
    return this.create({
      accountId: MapUtils.getString(map, 'accountId'),
      apiKeySecret: MapUtils.getStringOrNull(map, 'apiKeySecret'),
      invoiceTemplateId: MapUtils.getStringOrNull(map, 'invoiceTemplateId'),
      receiptTemplateId: MapUtils.getStringOrNull(map, 'receiptTemplateId'),
      vat: toEnumFromString(MapUtils.getString(map, 'vat'), InvoiceVat),
      allowLegalEntities: MapUtils.getBoolean(map, 'allowLegalEntities'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'accountId': this.accountId,
      'apiKeySecret': this.apiKeySecret,
      'provider': fromEnumAsString(this.provider),
      'vat': fromEnumAsString(this.vat),
      'invoiceTemplateId': this.invoiceTemplateId,
      'receiptTemplateId': this.receiptTemplateId,
      'allowLegalEntities': this.allowLegalEntities,
    };
  }

  clone(): this {
    return BillingoInvoicingProtocol.create({
      accountId: this.accountId,
      apiKeySecret: this.apiKeySecret,
      invoiceTemplateId: this.invoiceTemplateId,
      receiptTemplateId: this.receiptTemplateId,
      vat: this.vat,
      allowLegalEntities: this.allowLegalEntities,
    }) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.accountId === other.accountId &&
        this.apiKeySecret === other.apiKeySecret &&
        this.vat === other.vat &&
        this.allowLegalEntities === other.allowLegalEntities &&
        this.invoiceTemplateId === other.invoiceTemplateId &&
        this.receiptTemplateId === other.receiptTemplateId;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof BillingoInvoicingProtocol;
  }

  static assertType(dm: DataModel): dm is BillingoInvoicingProtocol {
    return dm instanceof BillingoInvoicingProtocol;
  }
}

export class BillingoInvoicingProtocolBuilder extends DataModelBuilder<BillingoInvoicingProtocol> {
  build(context: SerializationContext, map: SerializedData): BillingoInvoicingProtocol {
    return BillingoInvoicingProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BillingoInvoicingProtocol.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BillingoInvoicingProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [apiKeySecret] field was added.
    if(version < 2) {
      version = 2;
      MapUtils.set(map, 'apiKeySecret', null);
    }

    // In this version the [uniqueId] field was renamed to [accountId].
    if(version < 3) {
      version = 3;
      map['accountId'] = map['uniqueId'];
    }

    // In this version the [allowLegalEntities] field was added.
    if(version < 4) {
      version = 4;
      map['allowLegalEntities'] = false;
    }

    /**
     * Version 5:
     * - add `receiptTemplateId` of type `string | null`.
     */
    if(version < 5) {
      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
